import { rem } from 'polished';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { Logout, OpenExternalLink, Phone } from 'icons';
import { Breakpoints } from '@unmind/design-system/theme/deprecated';
import useViewportWidth from 'utils/useViewportWidth';
import { useUserEntitlements } from 'hooks/useUserEntitlements';
import { useLocation } from 'react-router';
import { extraSmall } from '@unmind/design-system';
import RoutePath from '../../App/RoutePath';
import { tracking } from '../../App/Tracking';
import useFeatureFlag from '../../flags/useFeatureFlag';
import { getChannel, UserType, isEmployee, isAdmin } from '../../utils';
import { useOrganisationEntitlements } from '../../Shared/Entitlement';
import { DropdownMenu, MenuItem } from './DropdownMenu';

const Wrapper = styled.div`
  display: flex;
  align-items: center;

  ${extraSmall(css`
    width: 100%;
    align-items: end;
    display: initial;
  `)}
`;

const ProfilesAndSettings = styled.div``;

const LinkContainer = styled.span`
  display: flex;
  align-items: center;
`;

const ExternalLinkIcon = styled(OpenExternalLink).attrs(() => ({
  primaryColor: 'currentColor',
}))`
  margin-left: ${rem(4)};
  height: ${`${4 / 3}em`};
  width: ${`${4 / 3}em`};
`;

const LogoutIcon = styled(Logout).attrs(({ theme }) => ({
  primaryColor: theme.colors.text.primary,
  height: 12,
  width: 12,
  ['aria-hidden']: true,
}))`
  margin-right: ${rem(8)};
`;

const HelpIcon = styled(Phone).attrs(({ theme }) => ({
  primaryColor: theme.colors.text.primary,
  height: 12,
  width: 12,
  ['aria-hidden']: true,
}))`
  margin-left: 6px;
`;

export type MenuItemConfig = {
  key: string;
  label: React.ReactNode;
  isEnabled?: boolean;
  path?: string;
  onSelect?(): void;
  overrides?: Record<string, unknown>;
};

export interface AccountMenuProps {
  permissions?: string[];
  userType?: UserType;
  onLogout(): void;
  onNavigate(path: string): void;
  isInVerticalMenu: boolean;
}

export const AccountMenu = ({
  permissions,
  userType,
  onLogout,
  onNavigate,
  isInVerticalMenu,
}: AccountMenuProps) => {
  const [dropdownMenuOpen, setDropdownMenuOpen] = useState(false);
  const { t: translate } = useTranslation('navigation');
  const { supportUrl } = getChannel();
  const plusOneEnabled = useFeatureFlag('plus-one');
  const workplaceInsightsEnabled = useFeatureFlag(
    'show-workplace-insights-nav-option',
  );
  const viewportWidth = useViewportWidth();
  const location = useLocation();

  const userIsAdmin = isAdmin(permissions);
  const { entitlements } = useOrganisationEntitlements([
    { entitlement: 'workplaceIndex' },
    { entitlement: 'standaloneIndex', default: true },
  ]);
  const { entitlements: userEntitlements } = useUserEntitlements();

  const isSmallScreen = viewportWidth < Breakpoints.S;

  const trackUnmindHelp = () => {
    tracking.track('help_immediate_support_clicked', {
      origin: location.pathname,
      unmindHelpEnabled: userEntitlements.unmindHelp,
      uiLocation: 'account-menu',
    });
  };

  const onOptionSelected = (menuItem: MenuItemConfig) => () => {
    const { key, path, label, onSelect } = menuItem;

    tracking.track('account-nav-menu-item-clicked', {
      menuItemKey: key,
      menuItemLabel: label,
    });

    if (onSelect) {
      onSelect();
    }

    if (path) {
      onNavigate(path);
    }
  };

  const toggleDropdownMenu = () => {
    setDropdownMenuOpen(!dropdownMenuOpen);
  };

  const closeDropdownMenu = () => {
    setDropdownMenuOpen(false);
  };

  const menuItemsConfig: MenuItemConfig[] = [
    {
      key: 'unmind-help',
      label: (
        <>
          {translate('account_menu.tabs.immediate_support')}
          <HelpIcon />
        </>
      ),
      path: userEntitlements.unmindHelp
        ? `${RoutePath.Help}#immediate-support`
        : RoutePath.Help,
      onSelect: trackUnmindHelp,
      isEnabled: isSmallScreen,
    },
    {
      key: 'account',
      label: translate('account_menu.tabs.account'),
      path: RoutePath.Account,
    },
    {
      key: 'plus-one',
      label: translate('account_menu.tabs.send_invite'),
      path: RoutePath.InviteColleague,
      isEnabled:
        Boolean(plusOneEnabled) &&
        isEmployee(userType) &&
        !entitlements.standaloneIndex,
    },
    {
      key: 'admin',
      label: translate('account_menu.tabs.admin'),
      path: RoutePath.Admin,
      isEnabled: userIsAdmin && !entitlements.standaloneIndex,
    },
    {
      key: 'analytics-suite',
      label: translate('account_menu.tabs.analytics_suite'),
      path: RoutePath.AnalyticsSuite,
      isEnabled:
        userEntitlements.supersetAnalytics && !entitlements.standaloneIndex,
    },
    {
      key: 'workplace-index',
      label: translate('account_menu.tabs.workplace_index'),
      path: RoutePath.Dashboard,
      isEnabled: Boolean(
        userIsAdmin && workplaceInsightsEnabled && entitlements.workplaceIndex,
      ),
    },
    {
      key: 'product-help',
      label: (
        <LinkContainer>
          {translate('account_menu.tabs.product_help.title')}{' '}
          <ExternalLinkIcon />
        </LinkContainer>
      ),
      onSelect: () => window.open(supportUrl),
      overrides: {
        as: 'a',
        'aria-label': translate('account_menu.tabs.product_help.a11y_label'),
        target: '_blank',
      },
    },
    {
      key: 'logout',
      label: (
        <LinkContainer>
          <LogoutIcon />
          {translate('account_menu.tabs.log_out')}
        </LinkContainer>
      ),
      onSelect: onLogout,
    },
  ];

  const menuItems = menuItemsConfig
    .filter(({ isEnabled }) => isEnabled !== false)
    .map(menuItem => (
      <MenuItem
        data-testid={`menu-item-${menuItem.key}`}
        key={menuItem.key}
        label={menuItem.label}
        onClick={onOptionSelected(menuItem)}
        {...menuItem.overrides}
      />
    ));

  return (
    <Wrapper>
      <ProfilesAndSettings
        data-testid="menu-profile-settings"
        id="menu-profile-settings"
      >
        <DropdownMenu
          onClick={toggleDropdownMenu}
          open={dropdownMenuOpen}
          onRequestClose={closeDropdownMenu}
          isInVerticalMenu={isInVerticalMenu}
        >
          {menuItems}
        </DropdownMenu>
      </ProfilesAndSettings>
    </Wrapper>
  );
};

export default AccountMenu;
